import { ScanSearch } from "lucide-react";
import { MaterialsList } from "@/components/wizard/ScrapedBox";
import { useEditor } from "./useEditorState";

const pct = (v) => `${Math.round((v || 0) * 100)}%`;

function Similarity({ s }) {
  if (!s?.project) return <p data-testid="gen-report-similarity">Nessun progetto esistente confrontabile.</p>;
  const tone = s.score >= 0.6 ? "text-amber-700" : "text-emerald-700";
  return (
    <p data-testid="gen-report-similarity">Somiglianza massima: <b className={tone}>{pct(s.score)}</b> con «{s.project}»
      {s.same?.length > 0 && <span className="text-slate-500"> · {s.same.join(", ")}</span>}</p>
  );
}

function PlanComposition({ c }) {
  const fixed = c.repaired ? "ripetizioni corrette in automatico con il modello leggero" : c.initial.length ? "nessuna correzione automatica" : "nessuna ripetizione";
  return (
    <div data-testid="gen-report-composition">
      <p>Piano compositivo: {c.checked} sezioni pianificate · {fixed}{c.repair_error ? " (correzione non riuscita)" : ""}</p>
      {c.remaining?.length > 0 && <ul className="mt-1 list-disc pl-4 space-y-0.5 text-amber-800" data-testid="gen-report-composition-remaining">
        {c.remaining.map((i, n) => <li key={n}>{i.page ? `${i.page} · ` : ""}{i.message}</li>)}</ul>}
    </div>
  );
}

// Final report of a deep generation: what was found on the client's site, what is missing, art direction, similarity.
export default function GenerationReport() {
  const r = useEditor().project.generation?.report;
  if (!r) return null;
  const ph = r.photos;
  return (
    <div className="rounded-lg border bg-white p-3 space-y-2.5 text-[11px] text-slate-700" data-testid="gen-report">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><ScanSearch size={12} />Analisi del cliente e originalità</p>
      {r.concept?.concept_name && <p data-testid="gen-report-concept"><b className="text-slate-900">{r.concept.concept_name}</b> · {r.concept.rationale}</p>}
      {r.concept?.differs_from_similar && <p className="text-slate-500">{r.concept.differs_from_similar}</p>}
      <Similarity s={r.similarity} />
      {r.composition && <PlanComposition c={r.composition} />}
      {r.research && <p className="text-slate-500">Sito analizzato: {r.research.pages} pagine di {r.research.url}</p>}
      {r.research?.materials?.length > 0 && <MaterialsList items={r.research.materials} testid="gen-report-materials" />}
      {r.photos_authorized && ph && <p data-testid="gen-report-photos">Foto del cliente importate: {ph.imported} di {ph.candidates} candidate ({ph.too_small} troppo piccole, {ph.duplicates} doppie){ph.logo ? " · logo importato" : ""}</p>}
      {r.photos_authorized && !ph && <p data-testid="gen-report-photos">Foto del cliente autorizzate, ma l'importazione non è disponibile per questa generazione.</p>}
      {r.research && !r.photos_authorized && <p data-testid="gen-report-photos">Foto del cliente non usate (uso non autorizzato): solo stock.</p>}
      {r.missing?.length > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-2" data-testid="gen-report-missing">
          <p className="font-semibold text-amber-900">Non recuperato (non inventato nella demo)</p>
          <ul className="mt-1 list-disc pl-4 space-y-0.5 text-amber-900/90">{r.missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
